import { ArrowRight, ScanSearch, Workflow } from "lucide-react";
import { Link } from "wouter";
import { type BlogConversionPath } from "@/data/contentArchitecture";
import { trackBlogCtaClick } from "@/lib/analytics";

type RelatedServicePathwayProps = {
  postSlug: string;
  path: BlogConversionPath;
};

const trailingSlash = (href: string) => href.endsWith("/") ? href : `${href}/`;

/**
 * A single, context-matched reverse conversion route for editorial articles.
 * It keeps service selection deterministic, offers a visible audit next step,
 * and avoids unsupported claims about rankings or AI-engine citations.
 */
export default function RelatedServicePathway({ postSlug, path }: RelatedServicePathwayProps) {
  const serviceHref = path.service ? trailingSlash(path.service.slug) : null;

  return (
    <section className="related-service-pathway mt-12 border border-emerald-500/25 bg-emerald-500/[0.04] p-6 md:p-8" aria-labelledby="related-service-heading" data-testid="related-service-pathway">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
        <Workflow className="h-4 w-4" aria-hidden="true" />
        Put this into practice
      </div>
      <h2 id="related-service-heading" className="mt-3 font-serif text-2xl text-foreground md:text-3xl">
        {path.service ? `Related service: ${path.service.name}` : "Turn this insight into a measurable next step"}
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">
        {path.service
          ? `${path.service.description} If this topic is a current commercial priority, start with the service scope, then use the audit to establish a transparent baseline for your website.`
          : "Use the Free AI Visibility Score to establish a transparent website baseline before choosing a service or content workstream."}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {serviceHref && path.service ? (
          <Link
            href={serviceHref}
            className="inline-flex items-center gap-2 bg-foreground px-5 py-3 text-sm font-semibold text-background hover:bg-foreground/85"
            onClick={() => trackBlogCtaClick({ cta_label: `Explore ${path.service?.name}`, destination: serviceHref, post_slug: postSlug })}
          >
            Explore the related service <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : null}
        <Link
          href={path.auditHref}
          className="inline-flex items-center gap-2 border border-emerald-600/45 px-5 py-3 text-sm font-semibold text-emerald-800 hover:border-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-200"
          onClick={() => trackBlogCtaClick({ cta_label: "Check my AI visibility", destination: path.auditHref, post_slug: postSlug })}
        >
          <ScanSearch className="h-4 w-4" aria-hidden="true" />
          Check my AI visibility
        </Link>
      </div>
    </section>
  );
}
